// src/components/FadeInSection.jsx
import React, { useState, useEffect, useRef } from 'react';

/**
 * FadeInSection: A reusable component that applies a fade-in and slide-up
 * animation to its children when they become visible in the viewport.
 * Elements will STAY visible after their initial fade-in.
 *
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - The content to render inside the section.
 * @param {number} [props.delay=0] - Delay before the animation starts (in seconds).
 * @param {number} [props.duration=0.8] - Duration of the fade-in animation (in seconds).
 * @param {object} [props.style={}] - Additional inline styles for the main div.
 */




function FadeInSection({ children, delay = 3, duration = 0.8, style = {} }) {
  const domRef = useRef(null);
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target); // Disconnect once seen
          }
        });
      },
      {
        threshold: 0.1, // Keep this, or set to 0 for earliest possible trigger
        // --- IMPORTANT CHANGE HERE ---
        // Change rootMargin to trigger earlier.
        // A negative bottom margin like "-100px" makes it trigger LATER.
        // To trigger EARLIER, use a positive bottom margin, or remove it entirely.
        // Option 1: Remove rootMargin, use default (0px 0px 0px 0px)
        // rootMargin: "0px",

        // Option 2: Expand the bottom boundary of the viewport to trigger earlier
        // For example, trigger when element is 50px *before* it enters the viewport
        rootMargin: "0px 0px 50px 0px" // This makes it trigger 50px *before* it hits the bottom of the root
      }
    );

    if (domRef.current) {
      observer.observe(domRef.current);
    }

    return () => {
      if (domRef.current) {
        observer.unobserve(domRef.current);
      }
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={domRef}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
        transition: `opacity ${duration}s ease-in-out ${delay}s, transform ${duration}s cubic-bezier(0, 0, 0.58, 1) ${delay}s`,
        height:'100%',
        padding:'0px',
        ...style
      }}
    >
      {children}
    </div>
  );
}
export default FadeInSection;